// Custom Graph の設定UIコンポーネント
// Grammar of Graphics準拠のレイヤーベースUI

import React, { useState, useEffect, useMemo } from 'react';
import { GraphConfigProps } from '../../../../types/graph-builder.types';
import {
  CustomGraphConfig,
  createDefaultScatterLayer,
  createDefaultLineLayer,
  createDefaultBarLayer,
  createDefaultAreaLayer,
  createDefaultTileLayer,
  LayerDef,
  GeomType,
  LineType,
  // ShapeType, // 将来使用予定
  PointGeomDef,
  LineGeomDef,
  BarGeomDef,
  AreaGeomDef,
  TileGeomDef,
  StatType,
  SmoothMethod,
  IdentityStatDef,
  BinStatDef,
  SmoothStatDef,
  CountStatDef,
  DensityStatDef,
  SummaryStatDef,
  SummaryFunction,
  StatAestheticChannel,
  PositionType,
  IdentityPositionDef,
  StackPositionDef,
  DodgePositionDef,
  JitterPositionDef,
  FillPositionDef,
  ScaleType,
  AxisScaleDef,
  ColorScaleDef,
  ColorScaleType,
  // SizeScaleDef, // 将来使用予定
  // ShapeScaleDef, // 将来使用予定
  // AlphaScaleDef, // 将来使用予定
  // LinetypeScaleDef, // 将来使用予定
  LegendPosition,
  CoordType,
  CoordDef,
  CartesianCoordDef,
  FlipCoordDef,
  FacetWrapDef,
  FacetGridDef,
  isContinuousScale,
  isCategoricalScale,
} from '../../../../types/custom-graph.types';
import { createColumnId, ColumnId, DatasetWithData } from '../../../../types';
import { getIntervalRatioColumns, getCategoricalColumns, getUniqueValueCounts } from '../../../utils/graph-builder-utils';
import { useSettingsStore } from '../../../stores/settingsStore';
import { getCategoricalPalettes, getSequentialPalettes, getDivergingPalettes } from '../../../constants/palette-utils';
import * as styles from '../../../styles/components/graph-builder.css.ts';

// 各 Geometry タイプがサポートする aesthetics を定義
type AestheticName = 'x' | 'y' | 'color' | 'fill' | 'stroke' | 'size' | 'shape' | 'alpha' | 'linetype';

function getSupportedAesthetics(geomType: GeomType): AestheticName[] {
  switch (geomType) {
    case 'point':
      return ['x', 'y', 'color', 'fill', 'stroke', 'size', 'shape', 'alpha'];
    case 'line':
      return ['x', 'y', 'color', 'size', 'linetype', 'alpha'];
    case 'bar':
      return ['x', 'y', 'fill', 'alpha'];
    case 'area':
      return ['x', 'y', 'fill', 'color', 'alpha'];
    case 'tile':
      return ['x', 'y', 'fill', 'alpha'];
    default:
      return ['x', 'y', 'color'];
  }
}

// 折りたたみ可能なセクションコンポーネント
interface CollapsibleSectionProps {
  id: string;
  title: string;
  defaultOpen?: boolean;
  isCollapsed?: boolean;
  onToggle?: (id: string, collapsed: boolean) => void;
  children: React.ReactNode;
}

function CollapsibleSection({ id, title, defaultOpen = false, isCollapsed, onToggle, children }: CollapsibleSectionProps) {
  // 外部から状態が渡されている場合はそれを使用、そうでなければローカル状態
  const [localIsOpen, setLocalIsOpen] = useState(defaultOpen);
  const isOpen = isCollapsed !== undefined ? !isCollapsed : localIsOpen;

  const handleToggle = () => {
    if (onToggle) {
      onToggle(id, isOpen); // 現在開いている場合は閉じる（collapsed = true）
    } else {
      setLocalIsOpen(!isOpen);
    }
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <button
        data-testid={`collapsible-section-${id}`}
        onClick={handleToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: '#f0f0f0',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333',
        }}
      >
        <span>{title}</span>
        <span style={{ fontSize: '16px', color: '#666' }}>{isOpen ? '▼' : '▶'}</span>
      </button>
      {isOpen && (
        <div style={{
          marginTop: '8px',
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e0e0e0'
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

export function CustomGraphConfigComponent({
  config,
  dataset,
  onChange
}: GraphConfigProps<CustomGraphConfig>) {
  // すべての列を取得（X/Y軸にはカテゴリカル変数も使用可能）
  const allColumns = dataset.schema.columns.map(col => ({ id: col.id, name: col.name }));
  // interval/ratio尺度の数値列のみを取得（Size/Alpha用）
  const numericColumns = getIntervalRatioColumns(dataset);
  // カテゴリカル列を取得（色分け用）
  const categoricalColumns = getCategoricalColumns(dataset);

  // Facet用: 全列とそのユニーク値数を計算
  const facetColumns = dataset.schema.columns;
  const facetColumnUniqueCounts = useMemo(() => {
    if (!dataset.data) return new Map<ColumnId, number>();
    const columnIds = facetColumns.map(col => col.id as ColumnId);
    return getUniqueValueCounts(dataset as DatasetWithData, columnIds);
  }, [dataset, facetColumns]);

  // Facetの最大カテゴリ数（グローバル設定から取得）
  const maxFacetCategories = useSettingsStore((state) => state.settings.graph.maxFacetCategories);

  // geom_tileが含まれるかどうかを判定
  const hasTileGeom = config.layers.some(l => l.geom.type === 'tile');
  // geom_tile用のX/Y列リスト（カテゴリカル + int64のみ）
  // 設計意図:
  // - geom_tileはヒートマップ・カレンダー・相関行列などカテゴリカル×カテゴリカルの可視化に使用
  // - タイルは隙間なく並ぶ必要があるため、バンドスケール（カテゴリカル）が適切
  // - int64を許可する理由: GitHubの草のような週番号（1-52）、月（1-12）などの整数列も使用可能にするため
  // - float64を除外する理由: 連続値でタイルを並べるユースケースは稀（2Dヒストグラムはgeom_bin2dの領域）
  const tileXYColumns = dataset.schema.columns
    .filter(col => col.type === 'string' || col.type === 'int64')
    .map(col => ({ id: col.id, name: col.name }));
  // X/Y用の列リスト（geom_tileの場合は制限）
  const xyColumns = hasTileGeom ? tileXYColumns : allColumns;

  // スケール設定の型ガード（UI表示用）
  const xScale = config.scales?.x;
  const yScale = config.scales?.y;
  const xIsContinuous = isContinuousScale(xScale);
  const yIsContinuous = isContinuousScale(yScale);
  const xDomain = xIsContinuous ? xScale.domain : undefined;
  const yDomain = yIsContinuous ? yScale.domain : undefined;
  const xTicks = xIsContinuous ? xScale.ticks : undefined;
  const yTicks = yIsContinuous ? yScale.ticks : undefined;

  // カテゴリカル軸のカテゴリを抽出
  const getCategories = (columnId: string | undefined): string[] => {
    if (!columnId || !dataset.data || !dataset.rowCount) return [];
    const column = dataset.schema.columns.find(col => col.id === columnId);
    if (!column) return [];

    // 順序尺度の場合、ordinalOrderを優先的に使用
    if (column.scale?.scale === 'ordinal' && column.scale.ordinalOrder) {
      // ordinalOrderをstring[]に変換して返す
      return column.scale.ordinalOrder.map(v => String(v));
    }

    // データから一意な値を抽出
    const uniqueValues = new Set<string>();
    const columnData = dataset.data[columnId as ColumnId];
    if (columnData) {
      for (let i = 0; i < dataset.rowCount; i++) {
        const value = columnData[i];
        if (value != null) {
          uniqueValues.add(String(value));
        }
      }
    }
    return Array.from(uniqueValues).sort((a, b) => {
      // 自然順ソートを適用（将来的にはgetCategoricalDomainを使用）
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b);
    });
  };

  const xCategories = !xIsContinuous ? getCategories(config.globalAes?.x as string) : [];
  const yCategories = !yIsContinuous ? getCategories(config.globalAes?.y as string) : [];

  // 初期化時に未定義のスケールを自動設定、または型が合わない場合に修正
  useEffect(() => {
    let needsUpdate = false;
    const newScales = { ...config.scales };

    // X軸のスケールをチェック
    if (config.globalAes?.x) {
      const column = dataset.schema.columns.find(col => col.id === config.globalAes?.x);
      if (column && column.scale) {
        const shouldBeCategorical = column.scale.scale === 'nominal' || column.scale.scale === 'ordinal';
        const currentScale = config.scales?.x;

        // ordinal列でordinalOrderがある場合はlimitsを計算
        const ordinalLimits = column.scale.scale === 'ordinal' && column.scale.ordinalOrder
          ? column.scale.ordinalOrder.map(v => String(v))
          : undefined;

        // スケールが未定義、または型が合わない場合に修正
        if (!currentScale || (shouldBeCategorical && !isCategoricalScale(currentScale)) || (!shouldBeCategorical && isCategoricalScale(currentScale))) {
          if (shouldBeCategorical) {
            // Issue#243: 順序尺度の順序をグラフに反映
            newScales.x = { type: 'categorical' as const, limits: ordinalLimits };
          } else {
            newScales.x = { type: 'linear' as const };
          }
          needsUpdate = true;
        } else if (shouldBeCategorical && isCategoricalScale(currentScale) && !currentScale.limits && ordinalLimits) {
          // Issue#243: 既存のカテゴリカルスケールにlimitsが未設定で、ordinalOrderがある場合に追加
          newScales.x = { ...currentScale, limits: ordinalLimits };
          needsUpdate = true;
        }
      }
    }

    // Y軸のスケールをチェック
    if (config.globalAes?.y) {
      const column = dataset.schema.columns.find(col => col.id === config.globalAes?.y);
      if (column && column.scale) {
        const shouldBeCategorical = column.scale.scale === 'nominal' || column.scale.scale === 'ordinal';
        const currentScale = config.scales?.y;

        // ordinal列でordinalOrderがある場合はlimitsを計算
        const ordinalLimits = column.scale.scale === 'ordinal' && column.scale.ordinalOrder
          ? column.scale.ordinalOrder.map(v => String(v))
          : undefined;

        // スケールが未定義、または型が合わない場合に修正
        if (!currentScale || (shouldBeCategorical && !isCategoricalScale(currentScale)) || (!shouldBeCategorical && isCategoricalScale(currentScale))) {
          if (shouldBeCategorical) {
            // Issue#243: 順序尺度の順序をグラフに反映
            newScales.y = { type: 'categorical' as const, limits: ordinalLimits };
          } else {
            newScales.y = { type: 'linear' as const };
          }
          needsUpdate = true;
        } else if (shouldBeCategorical && isCategoricalScale(currentScale) && !currentScale.limits && ordinalLimits) {
          // Issue#243: 既存のカテゴリカルスケールにlimitsが未設定で、ordinalOrderがある場合に追加
          newScales.y = { ...currentScale, limits: ordinalLimits };
          needsUpdate = true;
        }
      }
    }

    if (needsUpdate) {
      onChange({
        ...config,
        scales: newScales,
      });
    }
  }, [config.globalAes?.x, config.globalAes?.y, config.scales?.x, config.scales?.y, dataset.schema.columns, onChange, config]);

  // 折りたたみセクションの開閉状態を管理
  const collapsedSections = config.uiState?.collapsedSections;
  const isSectionCollapsed = (id: string) => {
    // collapsedSectionsが未定義（初期状態）の場合はundefinedを返す（defaultOpenを使用）
    if (collapsedSections === undefined) return undefined;
    return collapsedSections.includes(id);
  };

  const handleSectionToggle = (id: string, willBeCollapsed: boolean) => {
    const currentCollapsedSections = collapsedSections || [];
    const newCollapsedSections = willBeCollapsed
      ? [...currentCollapsedSections, id]
      : currentCollapsedSections.filter(sId => sId !== id);

    onChange({
      ...config,
      uiState: {
        ...config.uiState,
        collapsedSections: newCollapsedSections,
      },
    });
  };

  // Global Aestheticsの変更
  const handleGlobalAesChange = (
    channel: 'x' | 'y' | 'color' | 'fill' | 'stroke' | 'size' | 'shape' | 'alpha' | 'linetype' | 'group',
    value: string
  ) => {
    const newGlobalAes = {
      ...config.globalAes,
      [channel]: value ? createColumnId(value) : undefined,
    };

    // X軸またはY軸が選択された場合、MeasurementScaleに基づいてスケールを自動設定
    let newScales = config.scales;
    if ((channel === 'x' || channel === 'y') && value) {
      const column = dataset.schema.columns.find(col => col.id === value);
      if (column && column.scale) {
        const isCategorical = column.scale.scale === 'nominal' || column.scale.scale === 'ordinal';

        if (isCategorical) {
          // ordinal列の場合、ordinalOrderをlimitsに設定
          // Issue#243: 順序尺度の順序をグラフに反映
          const limits = column.scale.scale === 'ordinal' && column.scale.ordinalOrder
            ? column.scale.ordinalOrder.map(v => String(v))
            : undefined;
          newScales = {
            ...config.scales,
            [channel]: { type: 'categorical' as const, limits },
          };
        } else {
          newScales = {
            ...config.scales,
            [channel]: { type: 'linear' as const },
          };
        }
      }
    }

    onChange({
      ...config,
      globalAes: newGlobalAes,
      scales: newScales,
    });
  };

  // レイヤーの追加
  const handleAddLayer = () => {
    const newLayer = createDefaultScatterLayer();
    newLayer.name = `Layer ${config.layers.length + 1}`;
    onChange({
      ...config,
      layers: [...config.layers, newLayer],
    });
  };

  // レイヤーの削除
  const handleDeleteLayer = (index: number) => {
    const newLayers = config.layers.filter((_, i) => i !== index);
    onChange({
      ...config,
      layers: newLayers,
    });
  };

  // レイヤーの表示/非表示切り替え
  const handleToggleLayerVisible = (index: number) => {
    const newLayers = [...config.layers];
    newLayers[index] = {
      ...newLayers[index],
      visible: !newLayers[index].visible,
    };
    onChange({
      ...config,
      layers: newLayers,
    });
  };

  // レイヤーの並び替え（上へ）
  const handleMoveLayerUp = (index: number) => {
    if (index === 0) return;
    const newLayers = [...config.layers];
    [newLayers[index - 1], newLayers[index]] = [newLayers[index], newLayers[index - 1]];
    onChange({
      ...config,
      layers: newLayers,
    });
  };

  // レイヤーの並び替え（下へ）
  const handleMoveLayerDown = (index: number) => {
    if (index === config.layers.length - 1) return;
    const newLayers = [...config.layers];
    [newLayers[index], newLayers[index + 1]] = [newLayers[index + 1], newLayers[index]];
    onChange({
      ...config,
      layers: newLayers,
    });
  };

  // レイヤーの更新
  const handleUpdateLayer = (index: number, layer: LayerDef) => {
    const newLayers = [...config.layers];
    newLayers[index] = layer;
    onChange({
      ...config,
      layers: newLayers,
    });
  };

  // Scales設定の変更
  const handleScaleChange = (axis: 'x' | 'y' | 'y2', field: keyof AxisScaleDef, value: string | number | undefined) => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis] || {};

    let updatedAxisScale: AxisScaleDef;

    if (field === 'type') {
      updatedAxisScale = {
        ...currentAxisScale,
        type: value as ScaleType | undefined,
      };
    } else if (field === 'title') {
      updatedAxisScale = {
        ...currentAxisScale,
        title: value as string | undefined,
      };
    } else if (field === 'domain') {
      // domainは特別処理が必要
      updatedAxisScale = currentAxisScale;
    } else {
      updatedAxisScale = currentAxisScale;
    }

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: updatedAxisScale,
      },
    });
  };

  const handleScaleDomainChange = (axis: 'x' | 'y' | 'y2', minOrMax: 'min' | 'max', value: string) => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis] || {};
    const numValue = value === '' ? undefined : parseFloat(value);

    // domainはContinuousScaleDefにのみ存在
    if (!isContinuousScale(currentAxisScale)) return;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          domain: {
            ...currentAxisScale.domain,
            [minOrMax]: numValue,
          },
        },
      },
    });
  };

  const handleScaleTicksChange = (axis: 'x' | 'y' | 'y2', field: 'count' | 'format', value: string | number | undefined) => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis] || {};

    // ticksはContinuousScaleDefにのみ存在
    if (!isContinuousScale(currentAxisScale)) return;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          ticks: {
            ...currentAxisScale.ticks,
            [field]: value,
          },
        },
      },
    });
  };

  // カテゴリ順序変更のハンドラー
  const handleMoveCategoryUp = (axis: 'x' | 'y', index: number) => {
    if (index === 0) return; // 最初の要素は上に移動できない

    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    // 現在のlimitsを取得（未設定ならnatural sort順序で初期化）
    const categories = axis === 'x' ? xCategories : yCategories;
    const currentLimits = currentAxisScale.limits || categories;

    // 配列をコピーして並び替え
    const newLimits = [...currentLimits];
    [newLimits[index - 1], newLimits[index]] = [newLimits[index], newLimits[index - 1]];

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          limits: newLimits,
        },
      },
    });
  };

  const handleMoveCategoryDown = (axis: 'x' | 'y', index: number) => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    // 現在のlimitsを取得（未設定ならnatural sort順序で初期化）
    const categories = axis === 'x' ? xCategories : yCategories;
    const currentLimits = currentAxisScale.limits || categories;

    if (index === currentLimits.length - 1) return; // 最後の要素は下に移動できない

    // 配列をコピーして並び替え
    const newLimits = [...currentLimits];
    [newLimits[index], newLimits[index + 1]] = [newLimits[index + 1], newLimits[index]];

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          limits: newLimits,
        },
      },
    });
  };

  const handleResetCategoryLimits = (axis: 'x' | 'y') => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          limits: undefined,
        },
      },
    });
  };

  const handleReverseCategoryLimits = (axis: 'x' | 'y') => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    // 現在のlimitsを取得（未設定ならデフォルト順序で初期化）
    const categories = axis === 'x' ? xCategories : yCategories;
    const currentLimits = currentAxisScale.limits || categories;

    // 順序を逆転
    const reversed = [...currentLimits].reverse();

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          limits: reversed,
        },
      },
    });
  };

  // Breaks（表示カテゴリ選択）のハンドラー
  const handleToggleCategoryBreak = (axis: 'x' | 'y', category: string) => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    const categories = axis === 'x' ? xCategories : yCategories;
    const displayCategories = currentAxisScale.limits || categories;
    const currentBreaks = currentAxisScale.breaks || [];

    // 現在のbreaksにカテゴリが含まれているかチェック
    const isCurrentlyVisible = currentBreaks.length === 0 || currentBreaks.includes(category);

    let newBreaks: string[];
    if (isCurrentlyVisible) {
      // 非表示にする: breaksリストから削除
      // breaksが空の場合（全表示）は、表示したいカテゴリだけを含むリストを作成
      if (currentBreaks.length === 0) {
        newBreaks = displayCategories.filter(c => c !== category);
      } else {
        newBreaks = currentBreaks.filter(c => c !== category);
      }
    } else {
      // 表示にする: breaksリストに追加
      newBreaks = [...currentBreaks, category];
    }

    // 全カテゴリが選択されている場合はbreaksをundefinedに
    const allSelected = displayCategories.every(c => newBreaks.includes(c));

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          breaks: allSelected ? undefined : newBreaks,
        },
      },
    });
  };

  const handleSelectAllCategories = (axis: 'x' | 'y') => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          breaks: undefined, // 全選択はundefinedで表現
        },
      },
    });
  };

  const handleDeselectAllCategories = (axis: 'x' | 'y') => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          breaks: [], // 全解除は空配列
        },
      },
    });
  };

  // Labels（カスタムラベル）のハンドラー
  const handleCategoryLabelChange = (axis: 'x' | 'y', category: string, label: string) => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    const currentLabels = currentAxisScale.labels || {};

    // ラベルが空文字列の場合は削除
    const newLabels = { ...currentLabels };
    if (label === '') {
      delete newLabels[category];
    } else {
      newLabels[category] = label;
    }

    // 全てのラベルが削除された場合はundefined
    const hasAnyLabels = Object.keys(newLabels).length > 0;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          labels: hasAnyLabels ? newLabels : undefined,
        },
      },
    });
  };

  const handleClearAllLabels = (axis: 'x' | 'y') => {
    const currentScales = config.scales || {};
    const currentAxisScale = currentScales[axis];

    if (!isCategoricalScale(currentAxisScale)) return;

    onChange({
      ...config,
      scales: {
        ...currentScales,
        [axis]: {
          ...currentAxisScale,
          labels: undefined,
        },
      },
    });
  };

  const handleColorScaleChange = (field: keyof ColorScaleDef, value: string | undefined) => {
    const currentScales = config.scales || {};
    const currentColorScale = currentScales.color || {};

    onChange({
      ...config,
      scales: {
        ...currentScales,
        color: {
          ...currentColorScale,
          [field]: value,
        },
      },
    });
  };

  const handleFillScaleChange = (field: keyof ColorScaleDef, value: string | ColorScaleType | undefined) => {
    const currentScales = config.scales || {};
    const currentFillScale = currentScales.fill || {};

    onChange({
      ...config,
      scales: {
        ...currentScales,
        fill: {
          ...currentFillScale,
          [field]: value,
        },
      },
    });
  };

  // Coordinate System設定の変更
  const handleCoordChange = (coordType: CoordType) => {
    let newCoord: CoordDef;

    switch (coordType) {
      case 'cartesian':
        newCoord = { type: 'cartesian' } as CartesianCoordDef;
        break;
      case 'flip':
        newCoord = { type: 'flip' } as FlipCoordDef;
        break;
      default:
        newCoord = { type: 'cartesian' } as CartesianCoordDef;
    }

    onChange({
      ...config,
      coord: newCoord,
    });
  };

  // Facets設定の変更
  const handleFacetTypeChange = (facetType: 'none' | 'wrap' | 'grid') => {
    if (facetType === 'none') {
      onChange({
        ...config,
        facets: undefined,
      });
    } else if (facetType === 'wrap') {
      // デフォルトのFacet Wrap設定
      const firstCategoricalCol = categoricalColumns[0];
      onChange({
        ...config,
        facets: {
          type: 'wrap',
          variable: firstCategoricalCol ? createColumnId(firstCategoricalCol.id) : createColumnId(''),
        } as FacetWrapDef,
      });
    } else if (facetType === 'grid') {
      // デフォルトのFacet Grid設定
      const firstCategoricalCol = categoricalColumns[0];
      onChange({
        ...config,
        facets: {
          type: 'grid',
          cols: firstCategoricalCol ? createColumnId(firstCategoricalCol.id) : undefined,
        } as FacetGridDef,
      });
    }
  };

  const handleFacetWrapChange = (field: keyof FacetWrapDef, value: ColumnId | number | boolean | undefined) => {
    if (!config.facets || config.facets.type !== 'wrap') return;

    onChange({
      ...config,
      facets: {
        ...config.facets,
        [field]: value,
      } as FacetWrapDef,
    });
  };

  const handleFacetGridChange = (field: keyof FacetGridDef, value: ColumnId | number | boolean | undefined) => {
    if (!config.facets || config.facets.type !== 'grid') return;

    onChange({
      ...config,
      facets: {
        ...config.facets,
        [field]: value,
      } as FacetGridDef,
    });
  };

  return (
    <div className={styles.settingsSection}>
      {/* Global Aesthetics */}
      <CollapsibleSection
        id="globalAesthetics"
        title="Global Aesthetics"
        defaultOpen={true}
        isCollapsed={isSectionCollapsed('globalAesthetics')}
        onToggle={handleSectionToggle}
      >
        {/* Position (X, Y) - 常に表示 */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
            Position
          </div>
          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>X</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.x as string) || ''}
              onChange={(e) => handleGlobalAesChange('x', e.target.value)}
            >
              <option value="">None</option>
              {xyColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Y</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.y as string) || ''}
              onChange={(e) => handleGlobalAesChange('y', e.target.value)}
            >
              <option value="">None</option>
              {xyColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Color Channels - 折りたたみ可能、デフォルトで閉じる */}
        <CollapsibleSection
          id="globalAesthetics.color"
          title="Color"
          defaultOpen={false}
          isCollapsed={isSectionCollapsed('globalAesthetics.color')}
          onToggle={handleSectionToggle}
        >
          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Color</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.color as string) || ''}
              onChange={(e) => handleGlobalAesChange('color', e.target.value)}
            >
              <option value="">None</option>
              {categoricalColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Fill</label>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <select
                className={styles.settingSelect}
                style={{ flex: 1 }}
                value={
                  // Hex色の場合は"__fixed__"を選択状態にする
                  (config.globalAes?.fill as string)?.startsWith('#')
                    ? '__fixed__'
                    : (config.globalAes?.fill as string) || ''
                }
                onChange={(e) => {
                  if (e.target.value === '__fixed__') {
                    // 固定色を選択した場合、デフォルトの青を設定
                    handleGlobalAesChange('fill', '#3B82F6');
                  } else {
                    handleGlobalAesChange('fill', e.target.value);
                  }
                }}
              >
                <option value="">None</option>
                <option value="__fixed__">(Fixed Color)</option>
                {categoricalColumns.length > 0 && (
                  <optgroup label="Categorical">
                    {categoricalColumns.map(col => (
                      <option key={col.id} value={col.id}>
                        {col.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {numericColumns.length > 0 && (
                  <optgroup label="Numeric (for continuous scale)">
                    {numericColumns.map(col => (
                      <option key={col.id} value={col.id}>
                        {col.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {/* 固定色が選択されている場合はカラーピッカーを表示 */}
              {(config.globalAes?.fill as string)?.startsWith('#') && (
                <input
                  type="color"
                  value={(config.globalAes?.fill as string) || '#3B82F6'}
                  onChange={(e) => handleGlobalAesChange('fill', e.target.value)}
                  style={{ width: '32px', height: '24px', padding: 0, border: '1px solid #ccc', borderRadius: '2px', cursor: 'pointer' }}
                />
              )}
            </div>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Stroke</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.stroke as string) || ''}
              onChange={(e) => handleGlobalAesChange('stroke', e.target.value)}
            >
              <option value="">None</option>
              {categoricalColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
        </CollapsibleSection>

        {/* Advanced Channels - 折りたたみ可能、デフォルトで閉じる */}
        <CollapsibleSection
          id="globalAesthetics.advanced"
          title="Advanced"
          defaultOpen={false}
          isCollapsed={isSectionCollapsed('globalAesthetics.advanced')}
          onToggle={handleSectionToggle}
        >
          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Size</label>
            <select
              data-testid="size-select"
              className={styles.settingSelect}
              value={(config.globalAes?.size as string) || ''}
              onChange={(e) => handleGlobalAesChange('size', e.target.value)}
            >
              <option value="">None</option>
              {numericColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Shape</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.shape as string) || ''}
              onChange={(e) => handleGlobalAesChange('shape', e.target.value)}
            >
              <option value="">None</option>
              {categoricalColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Alpha</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.alpha as string) || ''}
              onChange={(e) => handleGlobalAesChange('alpha', e.target.value)}
            >
              <option value="">None</option>
              {numericColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Linetype</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.linetype as string) || ''}
              onChange={(e) => handleGlobalAesChange('linetype', e.target.value)}
            >
              <option value="">None</option>
              {categoricalColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel}>Group</label>
            <select
              className={styles.settingSelect}
              value={(config.globalAes?.group as string) || ''}
              onChange={(e) => handleGlobalAesChange('group', e.target.value)}
            >
              <option value="">None</option>
              {categoricalColumns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
        </CollapsibleSection>
      </CollapsibleSection>

      {/* Layers */}
      <CollapsibleSection
        id="layers"
        title="Layers"
        defaultOpen={true}
        isCollapsed={isSectionCollapsed('layers')}
        onToggle={handleSectionToggle}
      >
        {config.layers.map((layer, index) => (
          <LayerCard
            key={index}
            layer={layer}
            index={index}
            allColumns={allColumns}
            categoricalColumns={categoricalColumns}
            numericColumns={numericColumns}
            canMoveUp={index > 0}
            canMoveDown={index < config.layers.length - 1}
            onUpdate={(layer) => handleUpdateLayer(index, layer)}
            onToggleVisible={() => handleToggleLayerVisible(index)}
            onMoveUp={() => handleMoveLayerUp(index)}
            onMoveDown={() => handleMoveLayerDown(index)}
            onDelete={() => handleDeleteLayer(index)}
            config={config}
            onChange={onChange}
          />
        ))}

        <button
          onClick={handleAddLayer}
          style={{
            width: '100%',
            padding: '8px',
            marginTop: '8px',
            border: '1px dashed #ccc',
            borderRadius: '4px',
            background: 'white',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#666',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#4285f4';
            e.currentTarget.style.color = '#4285f4';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#ccc';
            e.currentTarget.style.color = '#666';
          }}
        >
          + Add Layer
        </button>
      </CollapsibleSection>

      {/* Scales Section */}
      <CollapsibleSection
        id="scales"
        title="Scales"
        defaultOpen={false}
        isCollapsed={isSectionCollapsed('scales')}
        onToggle={handleSectionToggle}
      >
        {/* X Axis */}
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          background: '#fff',
          borderRadius: '4px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
            {config.coord?.type === 'flip' ? 'X Axis (Vertical)' : 'X Axis'}
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Scale Type</label>
            {xIsContinuous ? (
              <select
                data-testid="x-axis-scale-type-select"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.scales?.x?.type || 'linear'}
                onChange={(e) => handleScaleChange('x', 'type', e.target.value as ScaleType)}
              >
                <option value="linear">Linear</option>
                <option value="log">Log</option>
                <option value="sqrt">Square Root</option>
                <option value="time">Time</option>
              </select>
            ) : (
              <div style={{ fontSize: '11px', padding: '4px', color: '#666' }}>
                {(() => {
                  const column = dataset.schema.columns.find(col => col.id === config.globalAes?.x);
                  if (column?.scale?.scale === 'nominal') return 'Nominal (auto)';
                  if (column?.scale?.scale === 'ordinal') return 'Ordinal (auto)';
                  return 'Categorical (auto)';
                })()}
              </div>
            )}
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Title</label>
            <input
              type="text"
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              placeholder="Auto"
              value={config.scales?.x?.title || ''}
              onChange={(e) => handleScaleChange('x', 'title', e.target.value || undefined)}
            />
          </div>

          {/* Domain and Ticks - only for continuous scales */}
          {xIsContinuous && (
            <>
              <div style={{ fontSize: '11px', marginBottom: '4px', color: '#666' }}>Domain</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Min</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="Auto"
                    value={xDomain?.min ?? ''}
                    onChange={(e) => handleScaleDomainChange('x', 'min', e.target.value)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Max</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="Auto"
                    value={xDomain?.max ?? ''}
                    onChange={(e) => handleScaleDomainChange('x', 'max', e.target.value)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
              </div>

              <div style={{ fontSize: '11px', marginBottom: '4px', color: '#666' }}>Ticks</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Count</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Auto"
                    value={xTicks?.count ?? ''}
                    onChange={(e) => handleScaleTicksChange('x', 'count', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Format</label>
                  <input
                    type="text"
                    placeholder="Auto"
                    value={xTicks?.format ?? ''}
                    onChange={(e) => handleScaleTicksChange('x', 'format', e.target.value || undefined)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Categorical Scale Settings */}
          {!xIsContinuous && xCategories.length > 0 && (() => {
            const xScaleDef = isCategoricalScale(xScale) ? xScale : undefined;
            const displayCategories = xScaleDef?.limits || xCategories;
            const hasCustomOrder = xScaleDef?.limits && xScaleDef.limits.length > 0;
            const xColumn = dataset.schema.columns.find(col => col.id === config.globalAes?.x);
            const isOrdinal = xColumn?.scale?.scale === 'ordinal';
            const orderType = hasCustomOrder ? 'Custom' : (isOrdinal ? 'Ordinal Order' : 'Natural Sort');

            return (
              <div style={{ marginTop: '12px', padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                {/* Category Order (Collapsible) */}
                <div style={{ marginBottom: '8px' }}>
                  <CollapsibleSection
                    id="x-category-order"
                    title={`Category Order (${orderType})`}
                    defaultOpen={false}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', gap: '4px' }}>
                      <button
                        onClick={() => handleReverseCategoryLimits('x')}
                        style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        Reverse
                      </button>
                      {hasCustomOrder && (
                        <button
                          onClick={() => handleResetCategoryLimits('x')}
                          style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <div style={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      fontSize: '10px',
                      padding: '4px',
                      background: '#fff',
                      borderRadius: '2px',
                      border: '1px solid #ddd'
                    }}>
                      {displayCategories.map((cat, idx) => (
                        <div
                          key={cat}
                          style={{
                            padding: '2px 4px',
                            borderBottom: idx < displayCategories.length - 1 ? '1px solid #f0f0f0' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span>{idx + 1}. {cat}</span>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button
                              onClick={() => handleMoveCategoryUp('x', idx)}
                              disabled={idx === 0}
                              style={{
                                fontSize: '10px',
                                padding: '0 4px',
                                background: idx === 0 ? '#f5f5f5' : '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '2px',
                                cursor: idx === 0 ? 'not-allowed' : 'pointer',
                                color: idx === 0 ? '#ccc' : '#333'
                              }}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => handleMoveCategoryDown('x', idx)}
                              disabled={idx === displayCategories.length - 1}
                              style={{
                                fontSize: '10px',
                                padding: '0 4px',
                                background: idx === displayCategories.length - 1 ? '#f5f5f5' : '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '2px',
                                cursor: idx === displayCategories.length - 1 ? 'not-allowed' : 'pointer',
                                color: idx === displayCategories.length - 1 ? '#ccc' : '#333'
                              }}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      {displayCategories.length} categories
                    </div>
                  </CollapsibleSection>
                </div>

                {/* Breaks (Visible Categories) - Collapsible */}
                <div style={{ marginBottom: '8px' }}>
                  <CollapsibleSection
                    id="x-visible-categories"
                    title="Visible Categories"
                    defaultOpen={false}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', marginBottom: '8px' }}>
                      <button
                        onClick={() => handleSelectAllCategories('x')}
                        style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        All
                      </button>
                      <button
                        onClick={() => handleDeselectAllCategories('x')}
                        style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        None
                      </button>
                    </div>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      fontSize: '10px',
                      padding: '4px',
                      background: '#fff',
                      borderRadius: '2px',
                      border: '1px solid #ddd'
                    }}>
                      {displayCategories.map((cat, idx) => {
                        const currentBreaks = xScaleDef?.breaks || [];
                        const isVisible = currentBreaks.length === 0 || currentBreaks.includes(cat);

                        return (
                          <div
                            key={cat}
                            style={{
                              padding: '2px 4px',
                              borderBottom: idx < displayCategories.length - 1 ? '1px solid #f0f0f0' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isVisible}
                              onChange={() => handleToggleCategoryBreak('x', cat)}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ color: isVisible ? '#333' : '#999' }}>{cat}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      {(() => {
                        const currentBreaks = xScaleDef?.breaks || [];
                        const visibleCount = currentBreaks.length === 0 ? displayCategories.length : currentBreaks.length;
                        return `${visibleCount} of ${displayCategories.length} visible`;
                      })()}
                    </div>
                  </CollapsibleSection>
                </div>

                {/* Labels (Custom Category Names) - Collapsible */}
                <div style={{ marginBottom: '8px' }}>
                  <CollapsibleSection
                    id="x-custom-labels"
                    title="Custom Labels"
                    defaultOpen={false}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      {xScaleDef?.labels && Object.keys(xScaleDef.labels).length > 0 && (
                        <button
                          onClick={() => handleClearAllLabels('x')}
                          style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      fontSize: '10px',
                      background: '#fff',
                      borderRadius: '2px',
                      border: '1px solid #ddd'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5' }}>
                            <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '40%' }}>
                              Category
                            </th>
                            <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              Custom Label
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayCategories.map((cat, idx) => {
                            const currentLabels = xScaleDef?.labels || {};
                            const customLabel = currentLabels[cat] || '';

                            return (
                              <tr key={cat} style={{ borderBottom: idx < displayCategories.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                <td style={{ padding: '4px', color: '#666' }}>{cat}</td>
                                <td style={{ padding: '4px' }}>
                                  <input
                                    type="text"
                                    value={customLabel}
                                    onChange={(e) => handleCategoryLabelChange('x', cat, e.target.value)}
                                    placeholder="(default)"
                                    style={{
                                      width: '100%',
                                      fontSize: '10px',
                                      padding: '2px 4px',
                                      border: '1px solid #ddd',
                                      borderRadius: '2px'
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      {(() => {
                        const currentLabels = xScaleDef?.labels || {};
                        const customCount = Object.keys(currentLabels).length;
                        return customCount > 0 ? `${customCount} custom label${customCount !== 1 ? 's' : ''}` : 'No custom labels';
                      })()}
                    </div>
                  </CollapsibleSection>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Y Axis */}
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          background: '#fff',
          borderRadius: '4px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
            {config.coord?.type === 'flip' ? 'Y Axis (Horizontal)' : 'Y Axis'}
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Scale Type</label>
            {yIsContinuous ? (
              <select
                data-testid="y-axis-scale-type-select"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.scales?.y?.type || 'linear'}
                onChange={(e) => handleScaleChange('y', 'type', e.target.value as ScaleType)}
              >
                <option value="linear">Linear</option>
                <option value="log">Log</option>
                <option value="sqrt">Square Root</option>
                <option value="time">Time</option>
              </select>
            ) : (
              <div style={{ fontSize: '11px', padding: '4px', color: '#666' }}>
                {(() => {
                  const column = dataset.schema.columns.find(col => col.id === config.globalAes?.y);
                  if (column?.scale?.scale === 'nominal') return 'Nominal (auto)';
                  if (column?.scale?.scale === 'ordinal') return 'Ordinal (auto)';
                  return 'Categorical (auto)';
                })()}
              </div>
            )}
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Title</label>
            <input
              type="text"
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              placeholder="Auto"
              value={config.scales?.y?.title || ''}
              onChange={(e) => handleScaleChange('y', 'title', e.target.value || undefined)}
            />
          </div>

          {/* Domain and Ticks - only for continuous scales */}
          {yIsContinuous && (
            <>
              <div style={{ fontSize: '11px', marginBottom: '4px', color: '#666' }}>Domain</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Min</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="Auto"
                    value={yDomain?.min ?? ''}
                    onChange={(e) => handleScaleDomainChange('y', 'min', e.target.value)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Max</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="Auto"
                    value={yDomain?.max ?? ''}
                    onChange={(e) => handleScaleDomainChange('y', 'max', e.target.value)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
              </div>

              <div style={{ fontSize: '11px', marginBottom: '4px', color: '#666' }}>Ticks</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Count</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Auto"
                    value={yTicks?.count ?? ''}
                    onChange={(e) => handleScaleTicksChange('y', 'count', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#888' }}>Format</label>
                  <input
                    type="text"
                    placeholder="Auto"
                    value={yTicks?.format ?? ''}
                    onChange={(e) => handleScaleTicksChange('y', 'format', e.target.value || undefined)}
                    style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Categorical Scale Settings */}
          {!yIsContinuous && yCategories.length > 0 && (() => {
            const yScaleDef = isCategoricalScale(yScale) ? yScale : undefined;
            const displayCategories = yScaleDef?.limits || yCategories;
            const hasCustomOrder = yScaleDef?.limits && yScaleDef.limits.length > 0;
            const yColumn = dataset.schema.columns.find(col => col.id === config.globalAes?.y);
            const isOrdinal = yColumn?.scale?.scale === 'ordinal';
            const orderType = hasCustomOrder ? 'Custom' : (isOrdinal ? 'Ordinal Order' : 'Natural Sort');

            return (
              <div style={{ marginTop: '12px', padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
                {/* Category Order (Collapsible) */}
                <div style={{ marginBottom: '8px' }}>
                  <CollapsibleSection
                    id="y-category-order"
                    title={`Category Order (${orderType})`}
                    defaultOpen={false}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', gap: '4px' }}>
                      <button
                        onClick={() => handleReverseCategoryLimits('y')}
                        style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        Reverse
                      </button>
                      {hasCustomOrder && (
                        <button
                          onClick={() => handleResetCategoryLimits('y')}
                          style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <div style={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      fontSize: '10px',
                      padding: '4px',
                      background: '#fff',
                      borderRadius: '2px',
                      border: '1px solid #ddd'
                    }}>
                      {displayCategories.map((cat, idx) => (
                        <div
                          key={cat}
                          style={{
                            padding: '2px 4px',
                            borderBottom: idx < displayCategories.length - 1 ? '1px solid #f0f0f0' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span>{idx + 1}. {cat}</span>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button
                              onClick={() => handleMoveCategoryUp('y', idx)}
                              disabled={idx === 0}
                              style={{
                                fontSize: '10px',
                                padding: '0 4px',
                                background: idx === 0 ? '#f5f5f5' : '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '2px',
                                cursor: idx === 0 ? 'not-allowed' : 'pointer',
                                color: idx === 0 ? '#ccc' : '#333'
                              }}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => handleMoveCategoryDown('y', idx)}
                              disabled={idx === displayCategories.length - 1}
                              style={{
                                fontSize: '10px',
                                padding: '0 4px',
                                background: idx === displayCategories.length - 1 ? '#f5f5f5' : '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '2px',
                                cursor: idx === displayCategories.length - 1 ? 'not-allowed' : 'pointer',
                                color: idx === displayCategories.length - 1 ? '#ccc' : '#333'
                              }}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      {displayCategories.length} categories
                    </div>
                  </CollapsibleSection>
                </div>

                {/* Breaks (Visible Categories) - Collapsible */}
                <div style={{ marginBottom: '8px' }}>
                  <CollapsibleSection
                    id="y-visible-categories"
                    title="Visible Categories"
                    defaultOpen={false}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', marginBottom: '8px' }}>
                      <button
                        onClick={() => handleSelectAllCategories('y')}
                        style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        All
                      </button>
                      <button
                        onClick={() => handleDeselectAllCategories('y')}
                        style={{
                          fontSize: '9px',
                          padding: '2px 6px',
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        None
                      </button>
                    </div>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      fontSize: '10px',
                      padding: '4px',
                      background: '#fff',
                      borderRadius: '2px',
                      border: '1px solid #ddd'
                    }}>
                      {displayCategories.map((cat, idx) => {
                        const currentBreaks = yScaleDef?.breaks || [];
                        const isVisible = currentBreaks.length === 0 || currentBreaks.includes(cat);

                        return (
                          <div
                            key={cat}
                            style={{
                              padding: '2px 4px',
                              borderBottom: idx < displayCategories.length - 1 ? '1px solid #f0f0f0' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isVisible}
                              onChange={() => handleToggleCategoryBreak('y', cat)}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ color: isVisible ? '#333' : '#999' }}>{cat}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      {(() => {
                        const currentBreaks = yScaleDef?.breaks || [];
                        const visibleCount = currentBreaks.length === 0 ? displayCategories.length : currentBreaks.length;
                        return `${visibleCount} of ${displayCategories.length} visible`;
                      })()}
                    </div>
                  </CollapsibleSection>
                </div>

                {/* Labels (Custom Category Names) - Collapsible */}
                <div style={{ marginBottom: '8px' }}>
                  <CollapsibleSection
                    id="y-custom-labels"
                    title="Custom Labels"
                    defaultOpen={false}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      {yScaleDef?.labels && Object.keys(yScaleDef.labels).length > 0 && (
                        <button
                          onClick={() => handleClearAllLabels('y')}
                          style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      fontSize: '10px',
                      background: '#fff',
                      borderRadius: '2px',
                      border: '1px solid #ddd'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5' }}>
                            <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '40%' }}>
                              Category
                            </th>
                            <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              Custom Label
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayCategories.map((cat, idx) => {
                            const currentLabels = yScaleDef?.labels || {};
                            const customLabel = currentLabels[cat] || '';

                            return (
                              <tr key={cat} style={{ borderBottom: idx < displayCategories.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                <td style={{ padding: '4px', color: '#666' }}>{cat}</td>
                                <td style={{ padding: '4px' }}>
                                  <input
                                    type="text"
                                    value={customLabel}
                                    onChange={(e) => handleCategoryLabelChange('y', cat, e.target.value)}
                                    placeholder="(default)"
                                    style={{
                                      width: '100%',
                                      fontSize: '10px',
                                      padding: '2px 4px',
                                      border: '1px solid #ddd',
                                      borderRadius: '2px'
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      {(() => {
                        const currentLabels = yScaleDef?.labels || {};
                        const customCount = Object.keys(currentLabels).length;
                        return customCount > 0 ? `${customCount} custom label${customCount !== 1 ? 's' : ''}` : 'No custom labels';
                      })()}
                    </div>
                  </CollapsibleSection>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Secondary Y Axis (Y2) */}
        {config.coord?.type !== 'flip' && (
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            background: '#fff',
            borderRadius: '4px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
              Secondary Y Axis (Right)
            </div>

            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
              <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Enabled</label>
              <input
                type="checkbox"
                checked={!!config.scales?.y2}
                onChange={(e) => {
                  const newScales = { ...config.scales };
                  if (e.target.checked) {
                    newScales.y2 = { type: 'linear' as const };
                  } else {
                    delete newScales.y2;
                  }
                  onChange({ ...config, scales: newScales });
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>

            {config.scales?.y2 && (
              <>
                <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
                  <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Scale Type</label>
                  <select
                    className={styles.settingSelect}
                    style={{ fontSize: '11px' }}
                    value={config.scales.y2.type || 'linear'}
                    onChange={(e) => handleScaleChange('y2', 'type', e.target.value as ScaleType)}
                  >
                    <option value="linear">Linear</option>
                    <option value="log">Log</option>
                    <option value="sqrt">Square Root</option>
                  </select>
                </div>

                <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
                  <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Title</label>
                  <input
                    type="text"
                    className={styles.settingSelect}
                    style={{ fontSize: '11px' }}
                    placeholder="Y2"
                    value={config.scales.y2.title || ''}
                    onChange={(e) => handleScaleChange('y2', 'title', e.target.value || undefined)}
                  />
                </div>

                <div style={{ fontSize: '11px', marginBottom: '4px', color: '#666' }}>Domain</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ fontSize: '10px', color: '#888' }}>Min</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="Auto"
                      value={isContinuousScale(config.scales.y2) ? config.scales.y2.domain?.min ?? '' : ''}
                      onChange={(e) => handleScaleDomainChange('y2', 'min', e.target.value)}
                      style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', color: '#888' }}>Max</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="Auto"
                      value={isContinuousScale(config.scales.y2) ? config.scales.y2.domain?.max ?? '' : ''}
                      onChange={(e) => handleScaleDomainChange('y2', 'max', e.target.value)}
                      style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                    />
                  </div>
                </div>

                <div style={{ fontSize: '11px', marginBottom: '4px', color: '#666' }}>Ticks</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={{ fontSize: '10px', color: '#888' }}>Count</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Auto"
                      value={isContinuousScale(config.scales.y2) ? config.scales.y2.ticks?.count ?? '' : ''}
                      onChange={(e) => handleScaleTicksChange('y2', 'count', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                      style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', color: '#888' }}>Format</label>
                    <input
                      type="text"
                      placeholder="Auto"
                      value={isContinuousScale(config.scales.y2) ? config.scales.y2.ticks?.format ?? '' : ''}
                      onChange={(e) => handleScaleTicksChange('y2', 'format', e.target.value || undefined)}
                      style={{ width: '100%', fontSize: '11px', padding: '4px' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Color Scale */}
        <div style={{
          padding: '12px',
          background: '#fff',
          borderRadius: '4px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
            Color Scale
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Palette</label>
            <select
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              value={config.scales?.color?.paletteId || ''}
              onChange={(e) => handleColorScaleChange('paletteId', e.target.value || undefined)}
            >
              <option value="">Default</option>
              <optgroup label="Categorical">
                {getCategoricalPalettes().map(palette => (
                  <option key={palette.id} value={palette.id}>{palette.name}</option>
                ))}
              </optgroup>
              <optgroup label="Sequential">
                {getSequentialPalettes().map(palette => (
                  <option key={palette.id} value={palette.id}>{palette.name}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Legend Position</label>
            <select
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              value={config.scales?.color?.legendPosition || 'right'}
              onChange={(e) => handleColorScaleChange('legendPosition', e.target.value as LegendPosition)}
            >
              <option value="right">Right</option>
              <option value="left">Left</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
              <option value="none">None</option>
            </select>
          </div>

          <div className={styles.settingItem}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Legend Title</label>
            <input
              type="text"
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              placeholder="Auto"
              value={config.scales?.color?.legendTitle || ''}
              onChange={(e) => handleColorScaleChange('legendTitle', e.target.value || undefined)}
            />
          </div>
        </div>

        {/* Fill Scale */}
        <div style={{
          padding: '12px',
          background: '#fff',
          borderRadius: '4px',
          border: '1px solid #e0e0e0',
          marginTop: '12px'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
            Fill Scale
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Scale Type</label>
            <select
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              value={config.scales?.fill?.scaleType || 'categorical'}
              onChange={(e) => handleFillScaleChange('scaleType', e.target.value as ColorScaleType)}
            >
              <option value="categorical">Categorical</option>
              <option value="sequential">Sequential (Continuous)</option>
              <option value="diverging">Diverging (Continuous)</option>
            </select>
          </div>

          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Palette</label>
            <select
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              value={config.scales?.fill?.paletteId || ''}
              onChange={(e) => handleFillScaleChange('paletteId', e.target.value || undefined)}
            >
              <option value="">Default</option>
              {config.scales?.fill?.scaleType === 'sequential' ? (
                <optgroup label="Sequential">
                  {getSequentialPalettes().map(palette => (
                    <option key={palette.id} value={palette.id}>{palette.name}</option>
                  ))}
                </optgroup>
              ) : config.scales?.fill?.scaleType === 'diverging' ? (
                <optgroup label="Diverging">
                  {getDivergingPalettes().map(palette => (
                    <option key={palette.id} value={palette.id}>{palette.name}</option>
                  ))}
                </optgroup>
              ) : (
                <>
                  <optgroup label="Categorical">
                    {getCategoricalPalettes().map(palette => (
                      <option key={palette.id} value={palette.id}>{palette.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Sequential">
                    {getSequentialPalettes().map(palette => (
                      <option key={palette.id} value={palette.id}>{palette.name}</option>
                    ))}
                  </optgroup>
                </>
              )}
            </select>
          </div>
        </div>
      </CollapsibleSection>

      {/* Facets Section */}
      <CollapsibleSection
        id="facets"
        title="Facets"
        defaultOpen={false}
        isCollapsed={isSectionCollapsed('facets')}
        onToggle={handleSectionToggle}
      >
        <div className={styles.settingItem}>
          <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Type</label>
          <select
            data-testid="facets-type-select"
            className={styles.settingSelect}
            style={{ fontSize: '11px' }}
            value={!config.facets ? 'none' : config.facets.type}
            onChange={(e) => handleFacetTypeChange(e.target.value as 'none' | 'wrap' | 'grid')}
          >
            <option value="none">None (Default)</option>
            <option value="wrap">Facet Wrap (Single Variable)</option>
            <option value="grid">Facet Grid (Two Variables)</option>
          </select>
        </div>

        {/* Facet Wrap設定 */}
        {config.facets?.type === 'wrap' && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: '#fff',
            borderRadius: '4px',
            border: '1px solid #e0e0e0'
          }}>
            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
              <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Variable</label>
              <select
                data-testid="facets-wrap-variable-select"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.facets.variable || ''}
                onChange={(e) => handleFacetWrapChange('variable', e.target.value ? createColumnId(e.target.value) : undefined)}
              >
                <option value="">Select a variable...</option>
                {facetColumns.map(col => {
                  const uniqueCount = facetColumnUniqueCounts.get(col.id as ColumnId) ?? 0;
                  const isTooMany = uniqueCount > maxFacetCategories;
                  return (
                    <option
                      key={col.id}
                      value={col.id}
                      disabled={isTooMany}
                    >
                      {col.name} ({uniqueCount}{isTooMany ? ' - too many' : ''})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
              <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Columns</label>
              <input
                type="number"
                min="1"
                placeholder="Auto"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.facets.ncol ?? ''}
                onChange={(e) => handleFacetWrapChange('ncol', e.target.value ? parseInt(e.target.value, 10) : undefined)}
              />
            </div>
            <div className={styles.settingItem}>
              <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Rows</label>
              <input
                type="number"
                min="1"
                placeholder="Auto"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.facets.nrow ?? ''}
                onChange={(e) => handleFacetWrapChange('nrow', e.target.value ? parseInt(e.target.value, 10) : undefined)}
              />
            </div>

            <div className={styles.settingItem} style={{ marginTop: '8px' }}>
              <label className={styles.checkboxLabel} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox"
                  data-testid="facets-wrap-complete-checkbox"
                  checked={config.facets.complete ?? false}
                  onChange={(e) => handleFacetWrapChange('complete', e.target.checked)}
                />
                Complete missing X×Y combinations
              </label>
            </div>

            <div style={{
              marginTop: '8px',
              padding: '8px',
              background: '#f9f9f9',
              borderRadius: '3px',
              fontSize: '11px',
              color: '#666',
              lineHeight: '1.4'
            }}>
              Split data by one variable and arrange panels in a grid layout
            </div>
          </div>
        )}

        {/* Facet Grid設定 */}
        {config.facets?.type === 'grid' && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: '#fff',
            borderRadius: '4px',
            border: '1px solid #e0e0e0'
          }}>
            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
              <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Rows</label>
              <select
                data-testid="facets-grid-rows-select"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.facets.rows || ''}
                onChange={(e) => handleFacetGridChange('rows', e.target.value ? createColumnId(e.target.value) : undefined)}
              >
                <option value="">None</option>
                {facetColumns.map(col => {
                  const uniqueCount = facetColumnUniqueCounts.get(col.id as ColumnId) ?? 0;
                  const isTooMany = uniqueCount > maxFacetCategories;
                  return (
                    <option
                      key={col.id}
                      value={col.id}
                      disabled={isTooMany}
                    >
                      {col.name} ({uniqueCount}{isTooMany ? ' - too many' : ''})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
              <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Columns</label>
              <select
                data-testid="facets-grid-cols-select"
                className={styles.settingSelect}
                style={{ fontSize: '11px' }}
                value={config.facets.cols || ''}
                onChange={(e) => handleFacetGridChange('cols', e.target.value ? createColumnId(e.target.value) : undefined)}
              >
                <option value="">None</option>
                {facetColumns.map(col => {
                  const uniqueCount = facetColumnUniqueCounts.get(col.id as ColumnId) ?? 0;
                  const isTooMany = uniqueCount > maxFacetCategories;
                  return (
                    <option
                      key={col.id}
                      value={col.id}
                      disabled={isTooMany}
                    >
                      {col.name} ({uniqueCount}{isTooMany ? ' - too many' : ''})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className={styles.settingItem} style={{ marginTop: '8px' }}>
              <label className={styles.checkboxLabel} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox"
                  data-testid="facets-grid-complete-checkbox"
                  checked={config.facets.complete ?? false}
                  onChange={(e) => handleFacetGridChange('complete', e.target.checked)}
                />
                Complete missing X×Y combinations
              </label>
            </div>

            <div style={{
              marginTop: '8px',
              padding: '8px',
              background: '#f9f9f9',
              borderRadius: '3px',
              fontSize: '11px',
              color: '#666',
              lineHeight: '1.4'
            }}>
              Create a 2D grid of panels using two variables (at least one required)
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Coordinate System Section */}
      <CollapsibleSection
        id="coordinateSystem"
        title="Coordinate System"
        defaultOpen={false}
        isCollapsed={isSectionCollapsed('coordinateSystem')}
        onToggle={handleSectionToggle}
      >
        <div className={styles.settingItem}>
          <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Type</label>
          <select
            data-testid="coordinate-system-type-select"
            className={styles.settingSelect}
            style={{ fontSize: '11px' }}
            value={config.coord?.type || 'cartesian'}
            onChange={(e) => handleCoordChange(e.target.value as CoordType)}
          >
            <option value="cartesian">Cartesian (Default)</option>
            <option value="flip">Flipped</option>
          </select>
        </div>

        {/* 各座標系の説明 */}
        <div style={{
          marginTop: '8px',
          padding: '8px',
          background: '#fff',
          borderRadius: '3px',
          fontSize: '11px',
          color: '#666',
          lineHeight: '1.4'
        }}>
          {(!config.coord || config.coord.type === 'cartesian') && (
            <span>Standard X-Y coordinate system</span>
          )}
          {config.coord?.type === 'flip' && (
            <span>Flips X and Y axes (useful for horizontal bar charts)</span>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}

// レイヤーカードコンポーネント
interface LayerCardProps {
  layer: LayerDef;
  index: number;
  allColumns: Array<{ id: string; name: string }>;
  categoricalColumns: Array<{ id: string; name: string }>;
  numericColumns: Array<{ id: string; name: string }>;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUpdate: (layer: LayerDef) => void;
  onToggleVisible: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  config: CustomGraphConfig;
  onChange: (config: CustomGraphConfig) => void;
}

function LayerCard({
  layer,
  index,
  allColumns,
  categoricalColumns,
  numericColumns,
  canMoveUp,
  canMoveDown,
  onUpdate,
  onToggleVisible,
  onMoveUp,
  onMoveDown,
  onDelete,
  config,
  onChange,
}: LayerCardProps) {
  // 折りたたみ状態管理
  const [geometryOpen, setGeometryOpen] = useState(true);
  const [aestheticsOpen, setAestheticsOpen] = useState(false);
  const [statisticsOpen, setStatisticsOpen] = useState(false);
  const [positionOpen, setPositionOpen] = useState(false);

  // レイヤー名の変更
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({
      ...layer,
      name: e.target.value,
    });
  };

  // Geomタイプの変更
  const handleGeomTypeChange = (newType: GeomType) => {
    let newGeom;
    switch (newType) {
      case 'point':
        newGeom = createDefaultScatterLayer().geom;
        break;
      case 'line':
        newGeom = createDefaultLineLayer().geom;
        break;
      case 'bar':
        newGeom = createDefaultBarLayer().geom;
        break;
      case 'area':
        newGeom = createDefaultAreaLayer().geom;
        break;
      case 'tile':
        newGeom = createDefaultTileLayer().geom;
        break;
      default:
        newGeom = createDefaultScatterLayer().geom;
    }
    onUpdate({
      ...layer,
      geom: newGeom,
    });
  };

  // Geometryパラメータの変更
  const handleGeomDefaultChange = (key: string, value: number | string) => {
    // Geomタイプごとにdefaultsを更新
    let newGeom;
    if (layer.geom.type === 'point') {
      const pointGeom = layer.geom as PointGeomDef;
      newGeom = {
        ...pointGeom,
        defaults: {
          ...pointGeom.defaults,
          [key]: value,
        },
      } as PointGeomDef;
    } else if (layer.geom.type === 'line') {
      const lineGeom = layer.geom as LineGeomDef;
      newGeom = {
        ...lineGeom,
        defaults: {
          ...lineGeom.defaults,
          [key]: value,
        },
      } as LineGeomDef;
    } else if (layer.geom.type === 'bar') {
      const barGeom = layer.geom as BarGeomDef;
      newGeom = {
        ...barGeom,
        defaults: {
          ...barGeom.defaults,
          [key]: value,
        },
      } as BarGeomDef;
    } else if (layer.geom.type === 'area') {
      const areaGeom = layer.geom as AreaGeomDef;
      newGeom = {
        ...areaGeom,
        defaults: {
          ...areaGeom.defaults,
          [key]: value,
        },
      } as AreaGeomDef;
    } else if (layer.geom.type === 'tile') {
      const tileGeom = layer.geom as TileGeomDef;
      newGeom = {
        ...tileGeom,
        defaults: {
          ...tileGeom.defaults,
          [key]: value,
        },
      } as TileGeomDef;
    } else {
      newGeom = layer.geom;
    }

    onUpdate({
      ...layer,
      geom: newGeom,
    });
  };

  // Aestheticマッピングの変更
  const handleAesChange = (channel: AestheticName, value: string) => {
    onUpdate({
      ...layer,
      aes: {
        ...layer.aes,
        [channel]: value ? createColumnId(value) : undefined,
      },
    });
  };

  // Statタイプの変更
  const handleStatTypeChange = (newType: StatType) => {
    let newStat;
    switch (newType) {
      case 'identity':
        newStat = { type: 'identity' } as IdentityStatDef;
        break;
      case 'bin':
        newStat = { type: 'bin', params: { bins: 30 } } as BinStatDef;
        break;
      case 'smooth':
        newStat = { type: 'smooth', params: { method: 'loess', se: true, span: 0.75, level: 0.95 } } as SmoothStatDef;
        break;
      case 'count':
        newStat = { type: 'count' } as CountStatDef;
        break;
      case 'density':
        newStat = { type: 'density', params: {} } as DensityStatDef;
        break;
      case 'summary':
        newStat = { type: 'summary', params: { fun: 'mean' } } as SummaryStatDef;
        break;
      default:
        newStat = { type: 'identity' } as IdentityStatDef;
    }
    onUpdate({
      ...layer,
      stat: newStat,
    });
  };

  // Statパラメータの変更
  const handleStatParamChange = (key: string, value: number | string | boolean | undefined) => {
    let newStat;
    if (layer.stat?.type === 'bin') {
      const binStat = layer.stat as BinStatDef;
      newStat = {
        ...binStat,
        params: {
          ...binStat.params,
          [key]: value,
        },
      } as BinStatDef;
    } else if (layer.stat?.type === 'smooth') {
      const smoothStat = layer.stat as SmoothStatDef;
      newStat = {
        ...smoothStat,
        params: {
          ...smoothStat.params,
          [key]: value,
        },
      } as SmoothStatDef;
    } else if (layer.stat?.type === 'density') {
      const densityStat = layer.stat as DensityStatDef;
      newStat = {
        ...densityStat,
        params: {
          ...densityStat.params,
          [key]: value,
        },
      } as DensityStatDef;
    } else if (layer.stat?.type === 'summary') {
      const summaryStat = layer.stat as SummaryStatDef;
      newStat = {
        ...summaryStat,
        params: {
          ...summaryStat.params,
          [key]: value,
        },
      } as SummaryStatDef;
    } else {
      newStat = layer.stat;
    }

    onUpdate({
      ...layer,
      stat: newStat,
    });
  };

  // Stat inputの変更
  const handleStatInputChange = (input: StatAestheticChannel | 'auto') => {
    if (!layer.stat) return;

    const newStat = {
      ...layer.stat,
      input: input === 'auto' ? undefined : input,
    };

    onUpdate({
      ...layer,
      stat: newStat,
    });
  };

  // Stat outputの変更
  const handleStatOutputChange = (output: StatAestheticChannel | 'auto') => {
    if (!layer.stat) return;

    const newStat = {
      ...layer.stat,
      output: output === 'auto' ? undefined : output,
    };

    onUpdate({
      ...layer,
      stat: newStat,
    });
  };

  // Positionタイプの変更
  const handlePositionTypeChange = (newType: PositionType) => {
    let newPosition;
    switch (newType) {
      case 'identity':
        newPosition = { type: 'identity' } as IdentityPositionDef;
        break;
      case 'stack':
        newPosition = { type: 'stack' } as StackPositionDef;
        break;
      case 'dodge':
        newPosition = { type: 'dodge', params: { width: 0.9 } } as DodgePositionDef;
        break;
      case 'jitter':
        newPosition = { type: 'jitter', params: { width: 0.4, height: 0.4 } } as JitterPositionDef;
        break;
      case 'fill':
        newPosition = { type: 'fill' } as FillPositionDef;
        break;
      default:
        newPosition = { type: 'identity' } as IdentityPositionDef;
    }
    onUpdate({
      ...layer,
      position: newPosition,
    });
  };

  // Positionパラメータの変更
  const handlePositionParamChange = (key: string, value: number) => {
    let newPosition;
    if (layer.position?.type === 'dodge') {
      const dodgePos = layer.position as DodgePositionDef;
      newPosition = {
        ...dodgePos,
        params: {
          ...dodgePos.params,
          [key]: value,
        },
      } as DodgePositionDef;
    } else if (layer.position?.type === 'jitter') {
      const jitterPos = layer.position as JitterPositionDef;
      newPosition = {
        ...jitterPos,
        params: {
          ...jitterPos.params,
          [key]: value,
        },
      } as JitterPositionDef;
    } else {
      newPosition = layer.position;
    }

    onUpdate({
      ...layer,
      position: newPosition,
    });
  };

  const visible = layer.visible ?? true;

  return (
    <div
      style={{
        marginBottom: '12px',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        background: 'white',
        opacity: visible ? 1 : 0.5,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
        <input
          type="checkbox"
          checked={visible}
          onChange={onToggleVisible}
          style={{ cursor: 'pointer' }}
        />
        <input
          type="text"
          value={layer.name || `Layer ${index + 1}`}
          onChange={handleNameChange}
          style={{
            flex: 1,
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '3px',
            fontSize: '13px',
            fontWeight: 'bold',
          }}
        />
        <button
          onClick={onMoveUp}
          disabled={!canMoveUp}
          title="Move up"
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '3px',
            background: 'white',
            cursor: canMoveUp ? 'pointer' : 'not-allowed',
            opacity: canMoveUp ? 1 : 0.3,
          }}
        >
          ↑
        </button>
        <button
          onClick={onMoveDown}
          disabled={!canMoveDown}
          title="Move down"
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '3px',
            background: 'white',
            cursor: canMoveDown ? 'pointer' : 'not-allowed',
            opacity: canMoveDown ? 1 : 0.3,
          }}
        >
          ↓
        </button>
        <button
          onClick={onDelete}
          title="Delete layer"
          style={{
            padding: '4px 8px',
            border: '1px solid #ddd',
            borderRadius: '3px',
            background: 'white',
            cursor: 'pointer',
            color: '#d32f2f',
          }}
        >
          ×
        </button>
      </div>

      {/* Geometry Section */}
      <div style={{ marginBottom: '12px' }}>
        <button
          onClick={() => setGeometryOpen(!geometryOpen)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 8px',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#666',
            marginBottom: '8px',
          }}
        >
          <span>Geometry</span>
          <span style={{ fontSize: '14px' }}>{geometryOpen ? '▼' : '▶'}</span>
        </button>

        {geometryOpen && (
          <>
            {/* Geomタイプ選択 */}
            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
          <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Type</label>
          <select
            data-testid="geometry-type-select"
            className={styles.settingSelect}
            style={{ fontSize: '11px' }}
            value={layer.geom.type}
            onChange={(e) => handleGeomTypeChange(e.target.value as GeomType)}
          >
            <option value="point">Point (Scatter)</option>
            <option value="line">Line</option>
            <option value="bar">Bar</option>
            <option value="area">Area</option>
            <option value="tile">Tile (Heatmap)</option>
          </select>
        </div>

        {/* Y Axis選択（Secondary Y軸が有効な場合のみ表示） */}
        {config.scales?.y2 && config.coord?.type !== 'flip' && (
          <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
            <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Y Axis</label>
            <select
              className={styles.settingSelect}
              style={{ fontSize: '11px' }}
              value={layer.yAxis ?? 'primary'}
              onChange={(e) => {
                const newLayers = [...config.layers];
                newLayers[index] = {
                  ...layer,
                  yAxis: e.target.value as 'primary' | 'secondary',
                };
                onChange({
                  ...config,
                  layers: newLayers,
                });
              }}
            >
              <option value="primary">Primary (Left)</option>
              <option value="secondary">Secondary (Right)</option>
            </select>
          </div>
        )}

        {/* Point parameters */}
        {layer.geom.type === 'point' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Size: {(layer.geom as PointGeomDef).defaults?.size ?? 5}
              </label>
              <input
                type="range"
                min="1"
                max="20"
                value={(layer.geom as PointGeomDef).defaults?.size ?? 5}
                onChange={(e) => handleGeomDefaultChange('size', parseInt(e.target.value, 10))}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Opacity: {((layer.geom as PointGeomDef).defaults?.alpha ?? 0.7).toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(layer.geom as PointGeomDef).defaults?.alpha ?? 0.7}
                onChange={(e) => handleGeomDefaultChange('alpha', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {/* Line parameters */}
        {layer.geom.type === 'line' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Stroke Width: {(layer.geom as LineGeomDef).defaults?.strokeWidth ?? 2}
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={(layer.geom as LineGeomDef).defaults?.strokeWidth ?? 2}
                onChange={(e) => handleGeomDefaultChange('strokeWidth', parseInt(e.target.value, 10))}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Line Type</label>
              <select
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
                value={(layer.geom as LineGeomDef).defaults?.lineType ?? 'solid'}
                onChange={(e) => handleGeomDefaultChange('lineType', e.target.value as LineType)}
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Opacity: {((layer.geom as LineGeomDef).defaults?.alpha ?? 0.9).toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(layer.geom as LineGeomDef).defaults?.alpha ?? 0.9}
                onChange={(e) => handleGeomDefaultChange('alpha', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {/* Bar parameters */}
        {layer.geom.type === 'bar' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Width: {((layer.geom as BarGeomDef).defaults?.width ?? 0.8).toFixed(1)}
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={(layer.geom as BarGeomDef).defaults?.width ?? 0.8}
                onChange={(e) => handleGeomDefaultChange('width', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Opacity: {((layer.geom as BarGeomDef).defaults?.alpha ?? 0.8).toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={(layer.geom as BarGeomDef).defaults?.alpha ?? 0.8}
                onChange={(e) => handleGeomDefaultChange('alpha', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {/* Area parameters */}
        {layer.geom.type === 'area' && (
          <div>
            <label style={{ fontSize: '11px', color: '#666' }}>
              Opacity: {((layer.geom as AreaGeomDef).defaults?.alpha ?? 0.5).toFixed(2)}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={(layer.geom as AreaGeomDef).defaults?.alpha ?? 0.5}
              onChange={(e) => handleGeomDefaultChange('alpha', parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        )}
          </>
        )}
      </div>

      {/* Aesthetics Section (Layer-specific overrides) */}
      <div style={{ marginBottom: '12px' }}>
        <button
          onClick={() => setAestheticsOpen(!aestheticsOpen)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 8px',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#666',
            marginBottom: '8px',
          }}
        >
          <span>Aesthetics (overrides global)</span>
          <span style={{ fontSize: '14px' }}>{aestheticsOpen ? '▼' : '▶'}</span>
        </button>

        {aestheticsOpen && (
          <div style={{
            padding: '8px',
            background: '#fafafa',
            borderRadius: '3px',
            fontSize: '12px'
          }}>
            {/* Geometry タイプに応じて表示する aesthetics を動的に生成 */}
            {getSupportedAesthetics(layer.geom.type).map((aesName, index) => {
              // 各 aesthetic に適した列リストを取得
              const getColumnsForAesthetic = (aes: AestheticName) => {
                switch (aes) {
                  case 'x':
                  case 'y':
                    return allColumns;
                  case 'size':
                  case 'alpha':
                    return numericColumns;
                  case 'color':
                  case 'fill':
                  case 'stroke':
                  case 'shape':
                  case 'linetype':
                    return categoricalColumns;
                  default:
                    return allColumns;
                }
              };

              const columns = getColumnsForAesthetic(aesName);
              const value = (layer.aes?.[aesName] as string) || '';

              // ラベルを大文字始まりに
              const label = aesName.charAt(0).toUpperCase() + aesName.slice(1);

              return (
                <div
                  key={aesName}
                  className={styles.settingItem}
                  style={{ marginBottom: index < getSupportedAesthetics(layer.geom.type).length - 1 ? '4px' : '0' }}
                >
                  <label className={styles.settingLabel} style={{ fontSize: '11px' }}>{label}</label>
                  <select
                    className={styles.settingSelect}
                    style={{ fontSize: '11px' }}
                    value={value}
                    onChange={(e) => handleAesChange(aesName, e.target.value)}
                  >
                    <option value="">Use global</option>
                    {columns.map((col: { id: string; name: string }) => (
                      <option key={col.id} value={col.id}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Statistics Section */}
      <div style={{ marginBottom: '12px' }}>
        <button
          onClick={() => setStatisticsOpen(!statisticsOpen)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 8px',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#666',
            marginBottom: '8px',
          }}
        >
          <span>Statistics</span>
          <span style={{ fontSize: '14px' }}>{statisticsOpen ? '▼' : '▶'}</span>
        </button>

        {statisticsOpen && (
          <>
            {/* Statタイプ選択 */}
            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
          <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Type</label>
          <select
            data-testid="statistics-type-select"
            className={styles.settingSelect}
            style={{ fontSize: '11px' }}
            value={layer.stat?.type || 'identity'}
            onChange={(e) => handleStatTypeChange(e.target.value as StatType)}
          >
            <option value="identity">Identity (No transformation)</option>
            <option value="bin">Bin (Histogram)</option>
            <option value="smooth">Smooth (Regression)</option>
            <option value="count">Count (Aggregation)</option>
            <option value="density">Density (KDE)</option>
            <option value="summary">Summary (Aggregation)</option>
          </select>
        </div>

        {/* Bin parameters */}
        {layer.stat?.type === 'bin' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#666' }}>
                  Bins: {(layer.stat as BinStatDef).params?.bins ?? 30}
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={(layer.stat as BinStatDef).params?.bins ?? 30}
                  onChange={(e) => handleStatParamChange('bins', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#666' }}>Bin Width</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Auto"
                  value={(layer.stat as BinStatDef).params?.binwidth ?? ''}
                  onChange={(e) => handleStatParamChange('binwidth', e.target.value ? parseFloat(e.target.value) : undefined)}
                  style={{ width: '100%', fontSize: '11px', padding: '2px' }}
                />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Y Scale</label>
              <select
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
                value={(layer.stat as BinStatDef).params?.yScale ?? 'count'}
                onChange={(e) => handleStatParamChange('yScale', e.target.value as 'count' | 'density')}
              >
                <option value="count">Count</option>
                <option value="density">Density</option>
              </select>
            </div>
          </>
        )}

        {/* Smooth parameters */}
        {layer.stat?.type === 'smooth' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Method</label>
              <select
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
                value={(layer.stat as SmoothStatDef).params?.method ?? 'loess'}
                onChange={(e) => handleStatParamChange('method', e.target.value as SmoothMethod)}
              >
                <option value="lm">Linear (LM)</option>
                <option value="loess">LOESS</option>
                <option value="gam">GAM</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Span: {((layer.stat as SmoothStatDef).params?.span ?? 0.75).toFixed(2)}
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={(layer.stat as SmoothStatDef).params?.span ?? 0.75}
                onChange={(e) => handleStatParamChange('span', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="checkbox"
                checked={(layer.stat as SmoothStatDef).params?.se ?? true}
                onChange={(e) => handleStatParamChange('se', e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <label style={{ fontSize: '11px', color: '#666' }}>Show confidence interval</label>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Confidence: {((layer.stat as SmoothStatDef).params?.level ?? 0.95) * 100}%
              </label>
              <input
                type="range"
                min="0.8"
                max="0.99"
                step="0.01"
                value={(layer.stat as SmoothStatDef).params?.level ?? 0.95}
                onChange={(e) => handleStatParamChange('level', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {/* Density parameters */}
        {layer.stat?.type === 'density' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Bandwidth</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Auto"
                value={(layer.stat as DensityStatDef).params?.bandwidth ?? ''}
                onChange={(e) => handleStatParamChange('bandwidth', e.target.value ? parseFloat(e.target.value) : undefined)}
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Y Scale</label>
              <select
                value={(layer.stat as DensityStatDef).params?.yScale ?? 'density'}
                onChange={(e) => handleStatParamChange('yScale', e.target.value as 'density' | 'count')}
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
              >
                <option value="density">Density</option>
                <option value="count">Count</option>
              </select>
            </div>
          </div>
        )}

        {/* Summary parameters */}
        {layer.stat?.type === 'summary' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Function</label>
              <select
                data-testid="summary-function-select"
                value={(layer.stat as SummaryStatDef).params?.fun ?? 'mean'}
                onChange={(e) => handleStatParamChange('fun', e.target.value as SummaryFunction)}
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
              >
                <option value="mean">Mean</option>
                <option value="sum">Sum</option>
                <option value="median">Median</option>
                <option value="min">Min</option>
                <option value="max">Max</option>
                <option value="count">Count</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Input (aggregate)</label>
              <select
                data-testid="summary-input-select"
                value={(layer.stat as SummaryStatDef).input ?? 'auto'}
                onChange={(e) => handleStatInputChange(e.target.value as StatAestheticChannel | 'auto')}
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
              >
                <option value="auto">Auto (Y)</option>
                <option value="y">Y</option>
                <option value="fill">Fill</option>
                <option value="color">Color</option>
                <option value="size">Size</option>
                <option value="alpha">Alpha</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>Output (result to)</label>
              <select
                data-testid="summary-output-select"
                value={(layer.stat as SummaryStatDef).output ?? 'auto'}
                onChange={(e) => handleStatOutputChange(e.target.value as StatAestheticChannel | 'auto')}
                style={{ width: '100%', fontSize: '11px', padding: '2px' }}
              >
                <option value="auto">Auto (same as Input)</option>
                <option value="y">Y</option>
                <option value="fill">Fill</option>
                <option value="color">Color</option>
                <option value="size">Size</option>
                <option value="alpha">Alpha</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                id="summary-complete"
                data-testid="summary-complete-checkbox"
                checked={(layer.stat as SummaryStatDef).params?.complete ?? false}
                onChange={(e) => handleStatParamChange('complete', e.target.checked)}
              />
              <label htmlFor="summary-complete" style={{ fontSize: '11px', color: '#666' }}>
                Fill missing X×Y with 0
              </label>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Position Section */}
      <div>
        <button
          onClick={() => setPositionOpen(!positionOpen)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 8px',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#666',
            marginBottom: '8px',
          }}
        >
          <span>Position</span>
          <span style={{ fontSize: '14px' }}>{positionOpen ? '▼' : '▶'}</span>
        </button>

        {positionOpen && (
          <>
            {/* Positionタイプ選択 */}
            <div className={styles.settingItem} style={{ marginBottom: '8px' }}>
          <label className={styles.settingLabel} style={{ fontSize: '11px' }}>Type</label>
          <select
            className={styles.settingSelect}
            style={{ fontSize: '11px' }}
            value={layer.position?.type || 'identity'}
            onChange={(e) => handlePositionTypeChange(e.target.value as PositionType)}
          >
            <option value="identity">Identity (No adjustment)</option>
            <option value="stack">Stack (Stacked)</option>
            <option value="dodge">Dodge (Side-by-side)</option>
            <option value="jitter">Jitter (Random offset)</option>
            <option value="fill">Fill (Normalized to 100%)</option>
          </select>
        </div>

        {/* Dodge parameters */}
        {layer.position?.type === 'dodge' && (
          <div>
            <label style={{ fontSize: '11px', color: '#666' }}>
              Width: {((layer.position as DodgePositionDef).params?.width ?? 0.9).toFixed(1)}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={(layer.position as DodgePositionDef).params?.width ?? 0.9}
              onChange={(e) => handlePositionParamChange('width', parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Jitter parameters */}
        {layer.position?.type === 'jitter' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Width: {((layer.position as JitterPositionDef).params?.width ?? 0.4).toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={(layer.position as JitterPositionDef).params?.width ?? 0.4}
                onChange={(e) => handlePositionParamChange('width', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#666' }}>
                Height: {((layer.position as JitterPositionDef).params?.height ?? 0.4).toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={(layer.position as JitterPositionDef).params?.height ?? 0.4}
                onChange={(e) => handlePositionParamChange('height', parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
